import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { apiService } from '../../services/api';

interface ClientExtendModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onSuccess: () => void;
}

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalYmd(ymd: string): Date | null {
  const m = String(ymd || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function unixToLocalDay(unix?: number | null): Date | null {
  if (!unix || Number(unix) <= 0) return null;
  return startOfLocalDay(new Date(Number(unix) * 1000));
}

function clampYmd(ymd: string, minYmd: string, maxYmd: string): string {
  if (!ymd) return minYmd;
  if (ymd < minYmd) return minYmd;
  if (ymd > maxYmd) return maxYmd;
  return ymd;
}

const ClientExtendModal: React.FC<ClientExtendModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess
}) => {
  const [today] = useState(() => {
    const n = new Date();
    return startOfLocalDay(n);
  });

  const currentExpDay = useMemo(
    () => unixToLocalDay(user?.exp_date),
    [user?.exp_date]
  );

  /** Base = vencimento actual (ou hoje se não houver). Máx = base + 3 dias. */
  const { minDate, maxDate, baseExpDay } = useMemo(() => {
    const base = currentExpDay || today;
    const max = new Date(base);
    max.setDate(max.getDate() + 3);

    let min = new Date(today.getFullYear(), today.getMonth(), 1);
    // Se o vencimento está muito no passado, a janela do mês actual não intersecta
    if (min.getTime() > max.getTime()) {
      min = new Date(base);
    }
    return { minDate: min, maxDate: max, baseExpDay: base };
  }, [currentExpDay, today]);

  const minYmd = formatLocalYmd(minDate);
  const maxYmd = formatLocalYmd(maxDate);
  const currentExpYmd = currentExpDay ? formatLocalYmd(currentExpDay) : '';

  const [expDate, setExpDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isTrial = Number(user?.is_trial) === 1;

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    const preferred = currentExpYmd || formatLocalYmd(baseExpDay);
    setExpDate(clampYmd(preferred, minYmd, maxYmd));
  }, [isOpen, currentExpYmd, baseExpDay, minYmd, maxYmd]);

  const handleSave = async () => {
    setError('');
    if (isTrial) {
      setError('Não é possível editar vencimento de linhas de teste');
      return;
    }

    const selected = parseLocalYmd(expDate);
    if (!selected) {
      setError('Seleccione uma data válida');
      return;
    }

    const minStart = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0);
    const maxEnd = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59);
    if (selected.getTime() < minStart.getTime()) {
      setError('Para trás só é permitido dentro do mês actual (ou a partir do vencimento actual)');
      return;
    }
    if (selected.getTime() > maxEnd.getTime()) {
      setError('No máximo 3 dias à frente da data de vencimento actual');
      return;
    }

    setLoading(true);
    try {
      await apiService.extendUser(user.id, { exp_date: expDate });
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Falha ao actualizar a data de vencimento';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isExpired = user?.exp_date && Number(user.exp_date) * 1000 < Date.now();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Editar vencimento
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Cliente</Label>
            <Input
              value={user?.username || ''}
              disabled
              className="bg-gray-50"
            />
          </div>

          <div>
            <Label>Vencimento actual</Label>
            <Input
              value={
                currentExpDay
                  ? new Date(Number(user.exp_date) * 1000).toLocaleString('pt-BR')
                  : 'Sem data'
              }
              disabled
              className="bg-gray-50"
            />
            {isExpired ? (
              <p className="text-xs text-red-600 mt-1">Linha expirada — pode editar na mesma</p>
            ) : (
              <p className="text-xs text-emerald-700 mt-1">Linha activa — também pode editar (1× por mês)</p>
            )}
          </div>

          {isTrial ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Linhas de teste não podem ter o vencimento editado por aqui.
            </div>
          ) : (
            <div>
              <Label htmlFor="exp_date">Nova data de vencimento</Label>
              <Input
                id="exp_date"
                type="date"
                value={expDate}
                min={minYmd}
                max={maxYmd}
                onChange={(e) => {
                  setExpDate(e.target.value);
                  setError('');
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                De <strong>{minDate.toLocaleDateString('pt-BR')}</strong> até{' '}
                <strong>{maxDate.toLocaleDateString('pt-BR')}</strong> (vencimento actual + 3 dias).
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Regras:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-1 space-y-1">
              <li>• Funciona com linha expirada ou activa</li>
              <li>• Máximo: 3 dias à frente do vencimento actual</li>
              <li>• Para trás: dentro do mês actual (quando aplicável)</li>
              <li>• Apenas 1 edição por mês nesta linha</li>
              <li>• Não aplica a testes · não consome créditos</li>
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              type="button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading || !expDate || isTrial}
              className="flex items-center gap-2"
              type="button"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Calendar className="w-4 h-4" />
              )}
              Guardar vencimento
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientExtendModal;
